import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { Clip, ClipPunchSlot } from "../models/Clip";

export const getClips = async (): Promise<Clip[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[FAB].[GetClips]", []);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as Omit<Clip, "Slots">[];
		return rows.map((row) => ({ ...row, Slots: [] }) as Clip);
	}
	throw new Error(resp.message || "Error fetching clips");
};

export const getClip = async (clipId: number): Promise<Clip | null> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "ClipId", value: clipId }];
	const resp = await apiService.callV2("[FAB].[GetClip]", parameters);

	if (resp.ok) {
		const parentRow = get(resp, "data.tables[0].rows[0]", null) as Omit<
			Clip,
			"Slots"
		> | null;
		if (!parentRow) return null;
		const slots = get(resp, "data.tables[1].rows", []) as ClipPunchSlot[];
		return { ...parentRow, Slots: slots } as Clip;
	}
	throw new Error(resp.message || "Error fetching clip");
};

export const createClip = async (clip: Partial<Clip>) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "ClipId", value: null },
		{ name: "ClipPartNumber", value: clip.ClipPartNumber ?? null },
		{
			name: "CustomerClipPartNumber",
			value: clip.CustomerClipPartNumber ?? null,
		},
		{ name: "Width", value: clip.Width ?? null },
		{ name: "Height", value: clip.Height ?? null },
		{ name: "Gauge", value: clip.Gauge ?? null },
		{ name: "CoilGauge", value: clip.CoilGauge ?? null },
		{ name: "CoilWidth", value: clip.CoilWidth ?? null },
		{ name: "WidthTolerance", value: clip.WidthTolerance ?? null },
		{ name: "HeightTolerance", value: clip.HeightTolerance ?? null },
		{ name: "MaterialGradeId", value: clip.MaterialGradeId ?? null },
		{ name: "TestCheckCycle", value: clip.TestCheckCycle ?? null },
		{ name: "DrawingNumber", value: clip.DrawingNumber ?? null },
		{ name: "DrawingPath", value: clip.DrawingPath ?? null },
		{ name: "DrawingRevision", value: clip.DrawingRevision ?? null },
		{
			name: "CustomerDrawingNumber",
			value: clip.CustomerDrawingNumber ?? null,
		},
		{
			name: "CustomerDrawingRevision",
			value: clip.CustomerDrawingRevision ?? null,
		},
		{ name: "HolePattern", value: clip.HolePattern ?? null },
		{ name: "SideGage", value: clip.SideGage ?? null },
		{ name: "EntryKeeperId", value: clip.EntryKeeperId ?? null },
		{ name: "ExitKeeperId", value: clip.ExitKeeperId ?? null },
		{ name: "BottomKeeperSetup", value: clip.BottomKeeperSetup ?? null },
		{ name: "DieSetDrawingNumber", value: clip.DieSetDrawingNumber ?? null },
		{
			name: "DieSetDrawingRevision",
			value: clip.DieSetDrawingRevision ?? null,
		},
		{ name: "DieSetNumber", value: clip.DieSetNumber ?? null },
		{ name: "Comments", value: clip.Comments ?? null },
		{ name: "Enabled", value: clip.IsActive ?? null },
	];
	return await apiService.callV2("[FAB].[UpsertClip]", parameters);
};

export const updateClip = async (
	clipId: number,
	fields: Record<string, string | number | boolean | null>,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "ClipId", value: clipId }];

	for (const [field, value] of Object.entries(fields)) {
		parameters.push({ name: field, value: value });
	}

	return await apiService.callV2("[FAB].[UpsertClip]", parameters);
};

export const setClipPunchSlots = async (
	clipId: number,
	slots: ClipPunchSlot[],
) => {
	const apiService = new MESApiService();
	const payload = slots.map((s) => ({
		ClipPunchSlotId: s.ClipPunchSlotId,
		HoleDia: s.HoleDia,
		Tolerance: s.Tolerance,
		PunchPartNumber: s.PunchPartNumber,
		ButtonPartNumber: s.ButtonPartNumber,
		Quantity: s.Quantity,
	}));
	const parameters: spParameter[] = [
		{ name: "ClipId", value: clipId },
		{ name: "Slots", value: JSON.stringify(payload) },
	];
	return await apiService.callV2("[FAB].[SetClipPunchSlots]", parameters);
};

export const deleteClip = async (clipId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "ClipId", value: clipId }];
	return await apiService.callV2("[FAB].[DeleteClip]", parameters);
};
